import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api, { apiError } from '../../services/api';
import { useApi } from '../../hooks/useApi';
import Select from '../../components/ui/Select';
import StatusPill from '../../components/StatusPill';
import { Card, EmptyState, LoadingRows, Notice, PageHeader, SearchInput, Toggle } from '../../components/ui/Page';
import { countryName } from '../../lib/countries';

export type DocRow = {
  id: string;
  countryCode: string;
  countryName: string;
  code: string;
  title: string;
  description: string | null;
  aiGuidance: string | null;
  category: 'FINANCIAL' | 'LEGAL' | 'KYC' | 'BUSINESS' | null;
  tracks: ('CORPORATE' | 'PARTNERSHIP' | 'PROPRIETORSHIP')[];
  required: boolean;
  isActive: boolean;
  sortOrder: number;
};

export type GroupAgent = {
  key: 'FINANCIAL' | 'LEGAL' | 'KYC' | 'BUSINESS';
  label: string;
  description: string;
  instructions: string;
  defaultInstructions: string;
  customised: boolean;
  updatedAt: string | null;
};

export const CATEGORIES = [
  { value: 'FINANCIAL', label: 'Financial', description: 'Statements, tax, banking, debt' },
  { value: 'LEGAL', label: 'Ownership & legal', description: 'Incorporation, governance, licences' },
  { value: 'KYC', label: 'Individuals & KYC', description: 'Promoters, partners, guarantors' },
  { value: 'BUSINESS', label: 'Business', description: 'Profile, pitch, market' },
] as const;

export const TRACKS = [
  { value: 'CORPORATE', label: 'Corporate' },
  { value: 'PARTNERSHIP', label: 'Partnership' },
  { value: 'PROPRIETORSHIP', label: 'Proprietorship' },
] as const;

const CATEGORY_LABEL: Record<string, string> = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));

export default function DocumentCountryPage() {
  const { country = '' } = useParams();
  const code = country.toUpperCase();
  const navigate = useNavigate();
  const { data, loading, error, reload, setData } = useApi<{ countryName: string | null; docs: DocRow[]; groups: GroupAgent[] }>(
    `/admin/documents/country/${code}`,
  );
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [track, setTrack] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const [agentDraft, setAgentDraft] = useState<Record<string, string>>({});
  const [openAgent, setOpenAgent] = useState<string | null>(null);
  const docs = data?.docs || [];
  const agents = data?.groups || [];
  const name = data?.countryName || countryName(code);

  useEffect(() => {
    if (!data?.groups?.length) return;
    setAgentDraft(Object.fromEntries(data.groups.map((a) => [a.key, a.instructions])));
  }, [data?.groups]);

  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    return docs.filter(
      (d) =>
        (!category || d.category === category) &&
        (!track || !d.tracks.length || d.tracks.includes(track as DocRow['tracks'][number])) &&
        (!t || [d.title, d.code, d.description].some((v) => v?.toLowerCase().includes(t))),
    );
  }, [docs, q, category, track]);

  const groups = useMemo(() => {
    const order = [...CATEGORIES.map((c) => c.value), null];
    return order
      .map((cat) => ({ cat, rows: shown.filter((d) => (d.category || null) === cat) }))
      .filter((g) => g.rows.length);
  }, [shown]);

  const patch = async (d: DocRow, body: Partial<Pick<DocRow, 'required' | 'isActive'>>) => {
    setBusy(d.id);
    setNotice(null);
    setData((prev) => prev && { ...prev, docs: prev.docs.map((x) => (x.id === d.id ? { ...x, ...body } : x)) });
    try {
      await api.patch(`/admin/documents/${d.id}`, body);
    } catch (e) {
      setNotice({ tone: 'error', text: apiError(e, 'Update failed.') });
      await reload();
    } finally {
      setBusy(null);
    }
  };

  const addStandard = async () => {
    setBusy('seed');
    setNotice(null);
    try {
      const { data: res } = await api.post(`/admin/documents/country/${code}/seed`, { countryName: name });
      setNotice({ tone: 'success', text: res.created ? `Added ${res.created} standard document(s).` : 'Every standard document is already on this checklist.' });
      await reload();
    } catch (e) {
      setNotice({ tone: 'error', text: apiError(e, 'Could not add standard documents.') });
    } finally {
      setBusy(null);
    }
  };

  const saveAgent = async (key: GroupAgent['key']) => {
    const instructions = (agentDraft[key] || '').trim();
    setBusy(`agent-${key}`);
    setNotice(null);
    try {
      const { data: res } = await api.put(`/admin/documents/country/${code}/groups/${key}`, { instructions });
      setData((prev) => prev && { ...prev, groups: res.groups });
      setNotice({ tone: 'success', text: `${CATEGORY_LABEL[key]} agent instructions saved.` });
    } catch (e) {
      setNotice({ tone: 'error', text: apiError(e, 'Could not save agent instructions.') });
    } finally {
      setBusy(null);
    }
  };

  const resetAgent = async (key: GroupAgent['key']) => {
    if (!window.confirm(`Reset the ${CATEGORY_LABEL[key]} agent to the Micus default instructions?`)) return;
    setBusy(`agent-${key}`);
    setNotice(null);
    try {
      const { data: res } = await api.delete(`/admin/documents/country/${code}/groups/${key}`);
      setData((prev) => prev && { ...prev, groups: res.groups });
      setNotice({ tone: 'success', text: `${CATEGORY_LABEL[key]} agent reset to defaults.` });
    } catch (e) {
      setNotice({ tone: 'error', text: apiError(e, 'Could not reset agent.') });
    } finally {
      setBusy(null);
    }
  };

  const active = docs.filter((d) => d.isActive);

  return (
    <div>
      <PageHeader
        crumbs={[{ label: 'Documents', to: '/documents' }, { label: name }]}
        title={`${name} checklist`}
        description={`${active.length} active document${active.length === 1 ? '' : 's'} in four groups. At rating time, each group runs a parallel validation agent; the lead model aggregates those results into the final summary.`}
        actions={
          <>
            <Link to={`/ratings/${code}`} className="mz-btn-ghost">
              Rating prompt
            </Link>
            <button type="button" className="mz-btn-ghost" disabled={busy === 'seed'} onClick={() => void addStandard()}>
              {busy === 'seed' ? 'Adding…' : 'Add standard documents'}
            </button>
            <Link to={`/documents/${code}/new?name=${encodeURIComponent(name)}`} className="mz-btn-primary">
              + Add document
            </Link>
          </>
        }
      />

      {(notice || error) && (
        <Notice tone={notice?.tone || 'error'} onClose={() => setNotice(null)} className="mb-4">
          {notice?.text || error}
        </Notice>
      )}

      <Card
        className="mz-fade-up mb-4"
        title="Parallel rating agents"
        subtitle="One agent per document group. Customise instructions for this country, or keep the Micus defaults."
        padded={false}
      >
        {loading && !data ? (
          <LoadingRows rows={4} />
        ) : (
          <div className="divide-y divide-[#fbeef4]">
            {agents.map((a) => {
              const open = openAgent === a.key;
              const count = docs.filter((d) => d.isActive && d.category === a.key).length;
              return (
                <div key={a.key} className="px-4 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <button type="button" className="min-w-0 text-left" onClick={() => setOpenAgent(open ? null : a.key)}>
                      <p className="font-semibold text-[#3b0a22]">
                        {a.label}
                        <span className="ml-2 text-xs font-medium text-[#a77d92]">{count} document{count === 1 ? '' : 's'}</span>
                      </p>
                      <p className="text-xs text-[#9a6b82]">{a.description}</p>
                    </button>
                    <div className="flex items-center gap-2">
                      <StatusPill label={a.customised ? 'Custom' : 'Default'} tone={a.customised ? 'pink' : 'slate'} />
                      <button type="button" className="text-xs font-semibold text-[#d6006e] hover:underline" onClick={() => setOpenAgent(open ? null : a.key)}>
                        {open ? 'Hide' : 'Edit'}
                      </button>
                    </div>
                  </div>
                  {open && (
                    <div className="mt-3 space-y-2">
                      <textarea
                        className="mz-input min-h-[140px] w-full font-mono text-[12.5px] leading-relaxed"
                        value={agentDraft[a.key] ?? a.instructions}
                        onChange={(e) => setAgentDraft((d) => ({ ...d, [a.key]: e.target.value }))}
                        aria-label={`${a.label} agent instructions`}
                      />
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          className="mz-btn-primary !px-3 !py-1.5 text-xs"
                          disabled={busy === `agent-${a.key}`}
                          onClick={() => void saveAgent(a.key)}
                        >
                          {busy === `agent-${a.key}` ? 'Saving…' : 'Save instructions'}
                        </button>
                        {a.customised && (
                          <button
                            type="button"
                            className="mz-btn-ghost !px-3 !py-1.5 text-xs"
                            disabled={busy === `agent-${a.key}`}
                            onClick={() => void resetAgent(a.key)}
                          >
                            Reset to default
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <Card padded={false} className="mz-fade-up">
        <div className="flex flex-wrap items-center gap-2 border-b border-[#fbeef4] p-3">
          <SearchInput value={q} onChange={setQ} placeholder="Search title, code or description…" className="min-w-[220px] flex-1" />
          <Select value={category} onChange={setCategory} className="w-52" options={[{ value: '', label: 'All categories' }, ...CATEGORIES]} />
          <Select
            value={track}
            onChange={setTrack}
            className="w-48"
            options={[{ value: '', label: 'All rating tracks' }, ...TRACKS.map((t) => ({ ...t, description: `Documents a ${t.label.toLowerCase()} company must upload` }))]}
          />
        </div>

        {loading && !data ? (
          <LoadingRows rows={6} />
        ) : docs.length === 0 ? (
          <EmptyState
            title="No documents yet"
            description="Start from the standard Micus pack or add documents one by one."
            action={
              <button type="button" className="mz-btn-primary" onClick={() => void addStandard()}>
                Add standard documents
              </button>
            }
          />
        ) : groups.length === 0 ? (
          <EmptyState title="No documents match the filters" />
        ) : (
          <div className="w-full overflow-x-auto">
            <table className="mz-table w-full">
              <thead>
                <tr>
                  <th>Document</th>
                  <th>Applies to</th>
                  <th>AI check</th>
                  <th className="text-center">Required</th>
                  <th className="text-center">Active</th>
                </tr>
              </thead>
              {groups.map((g) => (
                <tbody key={g.cat || 'none'}>
                  <tr>
                    <td colSpan={5} className="!bg-[#fdfafb] !py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#b77c98]">
                      {g.cat ? CATEGORY_LABEL[g.cat] : 'Uncategorised'} · {g.rows.length}
                      {g.cat && <span className="ml-2 font-medium normal-case tracking-normal text-[#c9a3b5]">parallel agent</span>}
                    </td>
                  </tr>
                  {g.rows.map((d) => (
                    <tr key={d.id} className={`mz-row-link ${d.isActive ? '' : 'opacity-55'}`} onClick={() => navigate(`/documents/${code}/${d.id}`)}>
                      <td className="max-w-[420px]">
                        <p className="font-semibold">{d.title}</p>
                        <p className="mt-0.5 line-clamp-1 text-xs text-[#a77d92]">
                          <span className="font-mono text-[11px] text-[#b77c98]">{d.code}</span>
                          {d.description ? ` · ${d.description}` : ''}
                        </p>
                      </td>
                      <td>
                        {d.tracks.length === 0 ? (
                          <span className="text-xs text-[#9a6b82]">All tracks</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {d.tracks.map((t) => (
                              <StatusPill key={t} label={t.slice(0, 4)} tone="slate" />
                            ))}
                          </div>
                        )}
                      </td>
                      <td>{d.aiGuidance ? <StatusPill label="Set" tone="green" /> : <StatusPill label="Missing" tone="amber" />}</td>
                      <td className="text-center" onClick={(e) => e.stopPropagation()}>
                        <Toggle size="sm" checked={d.required} disabled={busy === d.id} onChange={(v) => void patch(d, { required: v })} label="Required" />
                      </td>
                      <td className="text-center" onClick={(e) => e.stopPropagation()}>
                        <Toggle size="sm" checked={d.isActive} disabled={busy === d.id} onChange={(v) => void patch(d, { isActive: v })} label="Active" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              ))}
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
